<template>
  <div
    ref="scrollRef"
    class="extra-top-legend"
    :class="{ 'fade-top': canScrollUp, 'fade-bottom': canScrollDown }"
    @scroll.passive="updateOverflowState"
  >
    <slot name="header"></slot>
    <div v-for="(item, index) in props.items" :key="index" class="legend-item">
      <div class="label">
        {{ item.label }}
      </div>
      <div class="col value" :style="{ color: item.color }">
        {{ item.value.toLocaleString() }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';

import { useElementSize } from '@vueuse/core';

interface Items {
  label: string;
  value: number;
  color: string;
}

interface Props {
  items: Items[];
}

const props = defineProps<Props>();

const scrollRef = ref<HTMLElement | null>(null);
const { height: legendHeight } = useElementSize(scrollRef);

const canScrollUp = ref(false);
const canScrollDown = ref(false);

function updateOverflowState() {
  const el = scrollRef.value;
  if (!el) return;
  canScrollUp.value = el.scrollTop > 2;
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 2;
}

watch(
  [legendHeight, () => props.items],
  async () => {
    await nextTick();
    updateOverflowState();
  },
  { immediate: true },
);

</script>

<style lang="scss" scoped>
.extra-top-legend {
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  &.fade-bottom {
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
    mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
  }

  &.fade-top {
    -webkit-mask-image: linear-gradient(to bottom, transparent, #000 24px);
    mask-image: linear-gradient(to bottom, transparent, #000 24px);
  }

  &.fade-top.fade-bottom {
    -webkit-mask-image: linear-gradient(to bottom, transparent, #000 24px, #000 calc(100% - 24px), transparent);
    mask-image: linear-gradient(to bottom, transparent, #000 24px, #000 calc(100% - 24px), transparent);
  }

  .legend-item {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    margin-bottom: 4px;

    .label {
      overflow-wrap: anywhere;
    }
  }

  .value {
    text-align: end;
    overflow-wrap: anywhere;
    flex: 0 0 auto;
  }
}
</style>
